/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from "@solana/kit";
import {
  getAccountMetaFactory,
  type ResolvedInstructionAccount,
} from "@solana/program-client-core";
import { findEventAuthorityPda } from "../pdas/index.js";
import { CP_AMM_PROGRAM_ADDRESS } from "../programs/index.js";

export const UPDATE_REWARD_FUNDER_DISCRIMINATOR: ReadonlyUint8Array =
  new Uint8Array([211, 28, 48, 32, 215, 160, 35, 23]);

export function getUpdateRewardFunderDiscriminatorBytes(): ReadonlyUint8Array {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    UPDATE_REWARD_FUNDER_DISCRIMINATOR,
  );
}

export type UpdateRewardFunderInstruction<
  TProgram extends string = typeof CP_AMM_PROGRAM_ADDRESS,
  TAccountPool extends string | AccountMeta<string> = string,
  TAccountSigner extends string | AccountMeta<string> = string,
  TAccountEventAuthority extends string | AccountMeta<string> = string,
  TAccountProgram extends string | AccountMeta<string> =
    "cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountPool extends string
        ? WritableAccount<TAccountPool>
        : TAccountPool,
      TAccountSigner extends string
        ? ReadonlySignerAccount<TAccountSigner> &
            AccountSignerMeta<TAccountSigner>
        : TAccountSigner,
      TAccountEventAuthority extends string
        ? ReadonlyAccount<TAccountEventAuthority>
        : TAccountEventAuthority,
      TAccountProgram extends string
        ? ReadonlyAccount<TAccountProgram>
        : TAccountProgram,
      ...TRemainingAccounts,
    ]
  >;

export type UpdateRewardFunderInstructionData = {
  discriminator: ReadonlyUint8Array;
  rewardIndex: number;
  newFunder: Address;
};

export type UpdateRewardFunderInstructionDataArgs = {
  rewardIndex: number;
  newFunder: Address;
};

export function getUpdateRewardFunderInstructionDataEncoder(): FixedSizeEncoder<UpdateRewardFunderInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["rewardIndex", getU8Encoder()],
      ["newFunder", getAddressEncoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: UPDATE_REWARD_FUNDER_DISCRIMINATOR,
    }),
  );
}

export function getUpdateRewardFunderInstructionDataDecoder(): FixedSizeDecoder<UpdateRewardFunderInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["rewardIndex", getU8Decoder()],
    ["newFunder", getAddressDecoder()],
  ]);
}

export function getUpdateRewardFunderInstructionDataCodec(): FixedSizeCodec<
  UpdateRewardFunderInstructionDataArgs,
  UpdateRewardFunderInstructionData
> {
  return combineCodec(
    getUpdateRewardFunderInstructionDataEncoder(),
    getUpdateRewardFunderInstructionDataDecoder(),
  );
}

export type UpdateRewardFunderAsyncInput<
  TAccountPool extends string = string,
  TAccountSigner extends string = string,
  TAccountEventAuthority extends string = string,
  TAccountProgram extends string = string,
> = {
  pool: Address<TAccountPool>;
  signer: TransactionSigner<TAccountSigner>;
  eventAuthority?: Address<TAccountEventAuthority>;
  program?: Address<TAccountProgram>;
  rewardIndex: UpdateRewardFunderInstructionDataArgs["rewardIndex"];
  newFunder: UpdateRewardFunderInstructionDataArgs["newFunder"];
};

export async function getUpdateRewardFunderInstructionAsync<
  TAccountPool extends string,
  TAccountSigner extends string,
  TAccountEventAuthority extends string,
  TAccountProgram extends string,
  TProgramAddress extends Address = typeof CP_AMM_PROGRAM_ADDRESS,
>(
  input: UpdateRewardFunderAsyncInput<
    TAccountPool,
    TAccountSigner,
    TAccountEventAuthority,
    TAccountProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  UpdateRewardFunderInstruction<
    TProgramAddress,
    TAccountPool,
    TAccountSigner,
    TAccountEventAuthority,
    TAccountProgram
  >
> {
  // Program address.
  const programAddress = config?.programAddress ?? CP_AMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    pool: { value: input.pool ?? null, isWritable: true },
    signer: { value: input.signer ?? null, isWritable: false },
    eventAuthority: { value: input.eventAuthority ?? null, isWritable: false },
    program: { value: input.program ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.eventAuthority.value) {
    accounts.eventAuthority.value = await findEventAuthorityPda();
  }
  if (!accounts.program.value) {
    accounts.program.value =
      "cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG" as Address<"cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta("pool", accounts.pool),
      getAccountMeta("signer", accounts.signer),
      getAccountMeta("eventAuthority", accounts.eventAuthority),
      getAccountMeta("program", accounts.program),
    ],
    data: getUpdateRewardFunderInstructionDataEncoder().encode(
      args as UpdateRewardFunderInstructionDataArgs,
    ),
    programAddress,
  } as UpdateRewardFunderInstruction<
    TProgramAddress,
    TAccountPool,
    TAccountSigner,
    TAccountEventAuthority,
    TAccountProgram
  >);
}

export type UpdateRewardFunderInput<
  TAccountPool extends string = string,
  TAccountSigner extends string = string,
  TAccountEventAuthority extends string = string,
  TAccountProgram extends string = string,
> = {
  pool: Address<TAccountPool>;
  signer: TransactionSigner<TAccountSigner>;
  eventAuthority: Address<TAccountEventAuthority>;
  program?: Address<TAccountProgram>;
  rewardIndex: UpdateRewardFunderInstructionDataArgs["rewardIndex"];
  newFunder: UpdateRewardFunderInstructionDataArgs["newFunder"];
};

export function getUpdateRewardFunderInstruction<
  TAccountPool extends string,
  TAccountSigner extends string,
  TAccountEventAuthority extends string,
  TAccountProgram extends string,
  TProgramAddress extends Address = typeof CP_AMM_PROGRAM_ADDRESS,
>(
  input: UpdateRewardFunderInput<
    TAccountPool,
    TAccountSigner,
    TAccountEventAuthority,
    TAccountProgram
  >,
  config?: { programAddress?: TProgramAddress },
): UpdateRewardFunderInstruction<
  TProgramAddress,
  TAccountPool,
  TAccountSigner,
  TAccountEventAuthority,
  TAccountProgram
> {
  // Program address.
  const programAddress = config?.programAddress ?? CP_AMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    pool: { value: input.pool ?? null, isWritable: true },
    signer: { value: input.signer ?? null, isWritable: false },
    eventAuthority: { value: input.eventAuthority ?? null, isWritable: false },
    program: { value: input.program ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.program.value) {
    accounts.program.value =
      "cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG" as Address<"cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta("pool", accounts.pool),
      getAccountMeta("signer", accounts.signer),
      getAccountMeta("eventAuthority", accounts.eventAuthority),
      getAccountMeta("program", accounts.program),
    ],
    data: getUpdateRewardFunderInstructionDataEncoder().encode(
      args as UpdateRewardFunderInstructionDataArgs,
    ),
    programAddress,
  } as UpdateRewardFunderInstruction<
    TProgramAddress,
    TAccountPool,
    TAccountSigner,
    TAccountEventAuthority,
    TAccountProgram
  >);
}

export type ParsedUpdateRewardFunderInstruction<
  TProgram extends string = typeof CP_AMM_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    pool: TAccountMetas[0];
    signer: TAccountMetas[1];
    eventAuthority: TAccountMetas[2];
    program: TAccountMetas[3];
  };
  data: UpdateRewardFunderInstructionData;
};

export function parseUpdateRewardFunderInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedUpdateRewardFunderInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 4) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 4,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      pool: getNextAccount(),
      signer: getNextAccount(),
      eventAuthority: getNextAccount(),
      program: getNextAccount(),
    },
    data: getUpdateRewardFunderInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
